import { ButtonGroup, PrimaryButton, useColors } from "@certn/cascadia-react";
import { ajv } from "@certn/utils/src/ajv";
import { css } from "@emotion/css";
import {
  JsonFormsCore,
  JsonFormsProps,
  defaultErrorTranslator,
} from "@jsonforms/core";
import { JsonForms } from "@jsonforms/react";
import { vanillaCells } from "@jsonforms/vanilla-renderers";
import ajvTranslateToFr from "ajv-i18n/localize/fr";
import React, {
  forwardRef,
  LegacyRef,
  PropsWithChildren,
  useContext,
} from "react";
import { I18nProvider } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { CascadiaForm, CascadiaFormProps } from "./CascadiaForm";
import { CascadiaJsonFormContext } from "./CascadiaJsonFormContext";
import { baseRenderers } from "./baseRenderers";

interface Props extends PropsWithChildren {
  initialValues?: JsonFormsCore["data"];
  parentValues?: JsonFormsCore["data"];
  rootValues?: JsonFormsCore["data"];
  rootSchema?: JsonFormsCore["schema"];
  schema: JsonFormsCore["schema"];
  uischema?: JsonFormsCore["uischema"];
  renderers?: JsonFormsProps["renderers"];
  onSubmit?: CascadiaFormProps<unknown>["onSubmit"];
  onChange?: (values: object) => void;
  isSummary?: boolean;
  readonly?: boolean;
  formID?: string;
  locale?: string;
  isCheckResults?: boolean;
}

/**
 * Returns true when the given form path is currently in read-only "summary" mode.
 *
 * A path is considered in summary mode when:
 *   - the form-wide `globalIsSummary` flag is set, OR
 *   - the path is itself in `isSummaryOverride`, OR
 *   - the path is a descendant (by dot-segment boundary) of a path in
 *     `isSummaryOverride` — e.g. `criminal_cases.1.charges.0` inherits the
 *     summary state of its parent row `criminal_cases.1`.
 *
 * The boundary check (`overriddenPath + "."`) is load-bearing: a naive
 * `path.startsWith(overriddenPath)` produces false positives whenever one
 * path is a string prefix of another at the same nesting depth. For example,
 * `"criminal_cases.10".startsWith("criminal_cases.1")` is `true`, which made
 * "Edit Details" silently no-op for hits 11+ in the US county criminal QA
 * review (see FR-152). Always compare on segment boundaries.
 */
export function isSummaryForPath(
  path: string | undefined,
  isSummaryOverride: readonly string[],
  globalIsSummary?: boolean
): boolean {
  if (globalIsSummary) {
    return true;
  }
  if (path == null) {
    return false;
  }
  return isSummaryOverride.some(
    (overriddenPath) =>
      path === overriddenPath || path.startsWith(overriddenPath + ".")
  );
}

export const CascadiaJsonForm = forwardRef(function CascadiaJsonForm(
  props: Props,
  ref: LegacyRef<HTMLFormElement> | undefined
): React.JSX.Element {
  const colors = useColors();

  return (
    <CascadiaForm
      initialValues={props.initialValues}
      onSubmit={props.onSubmit}
      onChange={props.onChange}
      formID={props.formID}
      ref={ref}
    >
      {({
        values,
        setValues,
        handleSubmit,
        submitErrors,
        setSubmitErrors,
        touched,
        setTouched,
        submitted,
        setSubmitted,
        isSubmitting,
        isSummaryOverride,
        setIsSummaryOverride,
      }) => {
        return (
          <I18nProvider locale={props.locale}>
            <CascadiaJsonFormContext.Provider
              value={{
                schema: props.schema,
                uischema: props.uischema,
                renderers: props.renderers,
                values,
                parentValues: props.parentValues || {},
                rootSchema: props.rootSchema,
                rootValues: props.rootValues,
                setValues,
                initialValues: props.initialValues,
                submit: handleSubmit,
                touched,
                setTouched,
                isSummary: props.isSummary,
                isSummaryForPath: (path) =>
                  isSummaryForPath(path, isSummaryOverride, props.isSummary),
                readonly: props.readonly,
                isCheckResults: props.isCheckResults,
                isSubmitted: submitted,
                setIsSubmitted: setSubmitted,
                isSubmitting,
                formID: props.formID,
                locale: props.locale,
                fieldErrors: {},
                setFieldErrors: () => {},
                submitErrors: submitErrors || {},
                setSubmitErrors,
                setIsSummaryOverride,
              }}
            >
              {!props.children ? (
                <>
                  <CascadiaJsonFormContent />
                  <div
                    className={css(styles.actionBar, {
                      borderTop: `1px solid ${colors["border-base"]}`,
                      marginTop: "var(--space-5)",
                    })}
                  >
                    <ButtonGroup justify="end">
                      <PrimaryButton type="submit">Submit</PrimaryButton>
                    </ButtonGroup>
                  </div>
                </>
              ) : (
                props.children
              )}
            </CascadiaJsonFormContext.Provider>
          </I18nProvider>
        );
      }}
    </CascadiaForm>
  );
});

interface CascadiaJsonFormSubmitProps extends PropsWithChildren {
  testId?: string;
  isDisabled?: boolean;
}

export function CascadiaJsonFormSubmit(props: CascadiaJsonFormSubmitProps) {
  const { isSubmitting } = useContext(CascadiaJsonFormContext);
  return (
    <PrimaryButton
      testID={props.testId ? `${props.testId}-save-button` : "save-button"}
      type="submit"
      isDisabled={isSubmitting || props.isDisabled}
      isLoading={isSubmitting}
    >
      {props.children}
    </PrimaryButton>
  );
}

export function CascadiaJsonFormContent() {
  const { t, i18n } = useTranslation();
  const props = useContext(CascadiaJsonFormContext);

  const locale = props.locale ?? i18n.language;

  return (
    <JsonForms
      data={props.values}
      onChange={({ data }) => {
        props.setValues(data);
      }}
      uischema={props.uischema}
      schema={props.schema}
      renderers={[...baseRenderers, ...(props.renderers || [])]}
      readonly={props.readonly}
      cells={vanillaCells}
      validationMode="ValidateAndShow"
      ajv={ajv}
      i18n={{
        locale,
        translateError: (error, translate, uischema) => {
          if (
            error.keyword === "required" &&
            error.message?.startsWith("must have required property")
          ) {
            return t("is a required field");
          }
          if (
            locale &&
            locale.startsWith("fr") &&
            error.keyword !== "errorMessage"
          ) {
            /* ajvTranslateToFr is a function from ajv-i18n
            that takes an error message  and translates it in its place */
            ajvTranslateToFr([error]);
            // As a part of con-695 we want to understand what ajvTranslateToFr is doing
          }
          return defaultErrorTranslator(error, translate, uischema);
        },
      }}
    />
  );
}

const styles = {
  form: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
  }),
};
